import {ReactNode, useCallback, useEffect, useRef, useState} from "react";
import {Canvas, FabricObject} from "fabric";
import {Resource} from 'crqlar-react-common/dist/models/resource/resource.model';
import {Service} from 'crqlar-react-common/dist/models/service/service.model';
import {setupFabricDefaults} from "./utils/fabric-defaults";
import FloorplanContext from "./FloorplanContext";
import {FloorPlanItem} from "./types";
import {rebuildElement} from "./utils/fabric-factory";
import {isFloorplanTableObject} from "./utils/fabric-table-object";

export const FloorplanContextProvider = ({
                                             children,
                                             mode,
                                             showSeatsAroundTable,
                                             restaurantId,
                                             restaurantName = '',
                                             restaurantDraft = null,
                                             setRestaurantDraft = () => undefined,
                                             restaurantServices = [],
                                             setRestaurantServices = () => undefined,
                                             addedRestaurantServices = [],
                                             setAddedRestaurantServices = () => undefined,
                                             restaurantServicesLoading = false,
                                             removedRestaurantServiceIds = [],
                                             setRemovedRestaurantServiceIds = () => undefined,
                                             refetchRestaurantServices = async () => undefined,
                                         }: {
    children: ReactNode;
    mode: "read" | "edit";
    showSeatsAroundTable: boolean;
    restaurantId: string;
    restaurantName?: string;
    restaurantDraft?: Resource | null;
    setRestaurantDraft?: React.Dispatch<React.SetStateAction<Resource | null>>;
    restaurantServices?: Service[];
    setRestaurantServices?: React.Dispatch<React.SetStateAction<Service[]>>;
    addedRestaurantServices?: Service[];
    setAddedRestaurantServices?: React.Dispatch<React.SetStateAction<Service[]>>;
    restaurantServicesLoading?: boolean;
    removedRestaurantServiceIds?: string[];
    setRemovedRestaurantServiceIds?: React.Dispatch<React.SetStateAction<string[]>>;
    refetchRestaurantServices?: () => Promise<unknown>;
}) => {

    const containerRef = useRef<HTMLDivElement | null>(null);
    const canvasElRef = useRef<HTMLCanvasElement | null>(null);


    const [canvas, setCanvas] = useState<Canvas | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);


    useEffect(() => {
        const container = containerRef.current;
        const canvasEl = canvasElRef.current;

        if (!container || !canvasEl) return;

        const rect = container.getBoundingClientRect();
        const fabricCanvas = new Canvas(canvasEl, {
            width: rect.width,
            height: rect.height,
            backgroundColor: 'transparent',
            selection: true,
            selectionColor: 'rgba(66, 133, 244, 0.1)',
            selectionBorderColor: '#4285f4',
            selectionLineWidth: 2,
            enableRetinaScaling: true,
            imageSmoothingEnabled: false,
        });

        setupFabricDefaults();
        setCanvas(fabricCanvas);
        setIsInitialized(true);

        return () => {
            fabricCanvas.dispose();
            setCanvas(null);
            setIsInitialized(false);
        };
    }, []);


    // Keep Fabric in sync with both viewport changes and layout-only container changes.
    useEffect(() => {
        if (!canvas) return;

        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let lastMobile = window.innerWidth <= 900;

        const resizeCanvas = () => {
            const container = containerRef.current;
            if (!container) return;

            const rect = container.getBoundingClientRect();
            canvas.setDimensions({
                width: Math.max(rect.width, 100),
                height: Math.max(rect.height, 100),
            });
            canvas.renderAll();
        };

        const handleResize = () => {
            if (timeoutId) clearTimeout(timeoutId);
            const nowMobile = window.innerWidth <= 900;
            const delay = nowMobile !== lastMobile ? 50 : 500;
            lastMobile = nowMobile;

            timeoutId = setTimeout(() => {
                resizeCanvas();
            }, delay);
        };

        const container = containerRef.current;
        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== "undefined" && container) {
            resizeObserver = new ResizeObserver(resizeCanvas);
            resizeObserver.observe(container);
        }

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            resizeObserver?.disconnect();
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [canvas]);

    const updateTableElementLocally = useCallback((
        element: FabricObject,
        updater: (item: FloorPlanItem, currentElement: FabricObject) => FloorPlanItem
    ): FabricObject | null => {
        if (!canvas || !element.floorPlanItem?.table) return null;

        // Guard against stale element references: if the element has already been
        // removed from the canvas (e.g. by a previous rebuild whose React re-render
        // hasn't committed yet), resolve the current element by floorPlanItem id.
        let target = element;
        if (!canvas.getObjects().includes(element)) {
            const elementId = element.floorPlanItem?.id;
            if (!elementId) return null;
            const found = canvas.getObjects().find(
                (obj) => obj.floorPlanItem?.id === elementId
            );
            if (!found || !found.floorPlanItem?.table) return null;
            target = found as FabricObject;
        }

        if (isFloorplanTableObject(target)) {
            target.syncFloorPlanItemCanvas();
        }

        const currentItem = target.floorPlanItem;
        if (!currentItem?.table) return null;

        const nextItem = updater(currentItem, target);
        if (!nextItem.table) return null;

        const nextElement = rebuildElement(target, canvas, nextItem, showSeatsAroundTable);
        canvas.requestRenderAll();
        return nextElement as FabricObject;
    }, [canvas, showSeatsAroundTable]);

    const updateAllTableElementsLocally = useCallback((
        updater: (item: FloorPlanItem, currentElement: FabricObject) => FloorPlanItem
    ): FabricObject[] => {
        if (!canvas) return [];

        return [...canvas.getObjects()]
            .filter((obj): obj is FabricObject => !!obj.floorPlanItem?.table)
            .map((obj) => updateTableElementLocally(obj, updater))
            .filter((obj): obj is FabricObject => obj !== null);
    }, [canvas, updateTableElementLocally]);


    return (
        <FloorplanContext.Provider value={{
            mode,
            canvas,
            isInitialized,
            containerRef, // will be assigned to container html element
            canvasElRef, // will be assigned to canvas html element
            showSeatsAroundTable,
            restaurantId,
            restaurantName,
            restaurantDraft,
            setRestaurantDraft,
            restaurantServices,
            setRestaurantServices,
            addedRestaurantServices,
            setAddedRestaurantServices,
            restaurantServicesLoading,
            removedRestaurantServiceIds,
            setRemovedRestaurantServiceIds,
            refetchRestaurantServices,
            updateTableElementLocally,
            updateAllTableElementsLocally,
        }}>
            {children}
        </FloorplanContext.Provider>
    );
};
